import { ChangeDetectionStrategy, Component, effect, inject, OnInit, Signal, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgClass } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { takeUntil } from 'rxjs';

import { NgbCollapseModule } from '@ng-bootstrap/ng-bootstrap';

import { IOfficeFormHeaderInfo } from '@pages/office/models/office-data.model';
import { DestroyableDirective } from '@shared/directive/destroyable.directive';
import { IOfficeFormListItem } from '../models/form-list.models';
import { IOfficeCompany } from '../models/companies.model';
import { OfficeStore } from '../store/office.store';
import { IOfficeMenuItem } from '../models/office-nav.model';
import { INQUIRIES_MENU_ITEM } from './office-nav.constants';
import { RouterHelpers } from '../shared/helpers/router.helper';
import { OfficeRelatedPathsEnum } from '../office.constants';
import { OfficeLogoComponent } from './office-logo/office-logo.component';
import { ButtonDirective } from '../shared/components/button/button.directive';
import { ClickOutsideDirective } from '../shared/directives/click-outside.directive';

@Component({
  selector: 'app-office-nav',
  templateUrl: './office-nav.component.html',
  styleUrls: ['./office-nav.component.scss'],
  standalone: true,
  imports: [NgClass, ReactiveFormsModule, NgbCollapseModule, OfficeLogoComponent, ButtonDirective, ClickOutsideDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OfficeNavComponent extends DestroyableDirective implements OnInit {
  public isCollapsed = true;
  public selectedCompany$: Signal<number>;
  public preselectedModule: string;
  public companyList$: Signal<IOfficeCompany[]>;
  public groupedFormList$ = signal<IOfficeMenuItem[]>([]);
  public selectedForm: string;
  public selectedGroup$: Signal<IOfficeMenuItem>;
  public companyControl: FormControl<number>;

  readonly INQUIRIES_MENU_ITEM = INQUIRIES_MENU_ITEM;

  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private officeStore = inject(OfficeStore);
  private isPathPartOfRout = RouterHelpers.isPathPartOfRout();

  constructor() {
    super();
    effect(() => {
      this.selectedForm = this.officeStore.selectedForm$();
    });
    effect(
      () => {
        const formList = this.officeStore.formList$();
        const menuItems = this.getMenuItems(formList);
        this.groupedFormList$.set(menuItems);
      },
      { allowSignalWrites: true }
    );
  }

  ngOnInit(): void {
    this.companyList$ = this.officeStore.companyList$;
    this.selectedGroup$ = this.officeStore.selectedGroup$;
    this.companyControl = new FormControl<number>(this.officeStore.selectedCompany$());
    this.onCompanyChange();
  }

  public closeMenu(): void {
    this.isCollapsed = true;
  }

  public routeToReportGateway(): void {
    this.closeMenu();
    void this.router.navigate(['/office', this.officeStore.selectedCompany$(), 'reportgateway']);
  }

  public groupSelected(groupData: IOfficeMenuItem): void {
    if (groupData.name === INQUIRIES_MENU_ITEM.name) {
      this.closeMenu();
      this.officeStore.selectedForm$.set(null);
      this.officeStore.selectedGroup$.set(groupData);
      void this.router.navigate(['/office', this.officeStore.selectedCompany$(), 'inquiries']);
    } else {
      this.officeStore.selectedGroup$.set(groupData);
    }
  }

  public itemSelected(formData: IOfficeFormHeaderInfo): void {
    this.closeMenu();
    this.officeStore.formItemMetadata$.set(null);
    this.clearUpRelatedForm();
    void this.router.navigate([this.officeStore.selectedCompany$(), formData.FormName, 'grid'], { relativeTo: this.route });
  }

  private onCompanyChange(): void {
    this.companyControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(company => {
      this.officeStore.selectedGroup$.set(null);
      this.closeMenu();
      this.officeStore.selectedCompany$.set(company);
      const form = this.officeStore.parentForm$() ?? this.selectedForm;
      const remaining = form ?? 'index';
      this.clearUpRelatedForm();
      void this.router.navigate(
        [
          company,
          this.isPathPartOfRout(OfficeRelatedPathsEnum.inquiries) ??
            this.isPathPartOfRout(OfficeRelatedPathsEnum.reportgateway) ??
            remaining,
        ],
        { relativeTo: this.route }
      );
    });
  }

  private getMenuItems(formList: IOfficeFormListItem[]): IOfficeMenuItem[] {
    let menuItems = [];
    const groupByName: Record<string, IOfficeFormListItem[]> = formList.reduce((res, item) => {
      if (!res[item.ModuleLongName]) res[item.ModuleLongName] = [];
      res[item.ModuleLongName].push(item);

      if (this.selectedForm && this.selectedForm === item.FormName && this.preselectedModule !== item.ModuleShortName) {
        this.preselectedModule = item.ModuleShortName;
      }
      return { ...res };
    }, {});
    const groupedFormList = Object.entries(groupByName).reduce((res, [name, items]) => {
      const ModuleShortName = items[0]?.ModuleShortName;
      const formList: IOfficeFormListItem[][] = Object.values(
        items.reduce((listItems, item) => {
          if (!listItems[item.FormGroup]) listItems[item.FormGroup] = [];
          listItems[item.FormGroup].push(item);
          return listItems;
        }, {})
      );
      const group: IOfficeMenuItem = { name, formList, ModuleShortName };
      return [...res, group];
    }, []);
    // Add inquiries
    menuItems = [...groupedFormList, INQUIRIES_MENU_ITEM].sort((a, b) => {
      return a.name > b.name ? 1 : -1;
    });
    this.initPreselectedGroup(menuItems);
    return menuItems;
  }

  private initPreselectedGroup(menuItems: IOfficeMenuItem[]): void {
    if (this.isPathPartOfRout(OfficeRelatedPathsEnum.inquiries)) {
      this.preselectedModule = INQUIRIES_MENU_ITEM.ModuleShortName;
    }
    if (this.preselectedModule) {
      const preselectedGroup = menuItems.find(({ ModuleShortName }) => ModuleShortName === this.preselectedModule);
      this.officeStore.selectedGroup$.set(preselectedGroup);
    }
  }

  private clearUpRelatedForm(): void {
    this.officeStore.relatedToForm$.set(null);
    this.officeStore.parentForm$.set(null);
    this.officeStore.parentFormData$.set(null);
  }
}
